import type { DTO, Nullable } from '@/type-utils';
import type IGiftCardProduct from '@/services/models/Product/IGiftCardProduct';
import { InvalidArgumentError, InvalidStateError } from '@/utils/errors';
import { isNullOrEmpty } from '@/utils/null-utils';
import { PriceModel } from '@/services/models/Price';
import { MoneyModel } from '@/services/models/Money';
import LineItemModel from '../LineItemModel';
import type { IGiftCardLineItem } from './types';
import LineItemType from '../LineItemType';
import type { ICart } from '../..';

/**
 * Model for handling a line item which is specific for a gift card.
 */
export default class GiftCardLineItemModel
  extends LineItemModel<DTO<IGiftCardLineItem>>
  implements IGiftCardLineItem
{
  /** @inheritdoc */
  public readonly recipientEmail: string;
  /** @inheritdoc */
  public readonly recipientName: string;
  /** @inheritdoc */
  public readonly senderName: string;
  /** @inheritdoc */
  public readonly imageUrl: string;
  /** @inheritdoc */
  public readonly message?: string;
  /** @inheritdoc */
  public override readonly type = LineItemType.GiftCard;
  /** @inheritdoc */
  public price: Nullable<number>;

  /** @inheritdoc */
  public constructor(dto: DTO<IGiftCardLineItem>) {
    super(dto);
    this.recipientEmail = dto.giftCardRecipientEmail ?? '';
    this.recipientName = dto.giftCardRecipientName ?? '';
    this.senderName = dto.giftCardSenderName ?? '';
    this.message = dto.giftMessage ?? undefined;
    this.imageUrl = dto.giftCardImageUrl ?? '';
    this.price = dto.price;
  }

  /**
   * Creates a line item from a gift card product.
   *
   * @param product - The product to create the line item from.
   * @param uuid - The uuid for this item, it should come from the cart api.
   * @param cart - The cart the line item will belong to.
   *
   * @returns A new {@link GiftCardLineItemModel} created from the supplied product.
   * @throws An {@link InvalidArgumentError} if the supplied product is missing required data.
   */
  public static fromProduct(
    product: IGiftCardProduct,
    uuid: string,
    cart: ICart | string
  ): GiftCardLineItemModel {
    const {
      sku,
      name,
      price,
      group,
      upc,
      taxClass,
      message,
      recipientEmail,
      recipientName,
      senderName,
      imageUrl
    } = product;
    if (!price)
      throw new InvalidArgumentError(
        'The supplied product does not have a price.'
      );

    if (isNullOrEmpty(upc)) {
      throw new InvalidStateError(
        `The product: ${sku} has no UPC and therefore cannot be added to the cart.`
      );
    }

    const cartID = typeof cart === 'string' ? cart : cart.uuid;

    return new GiftCardLineItemModel({
      type: LineItemType.GiftCard,
      upc,
      uuid,
      cartID,
      sku,
      name,
      image: { src: imageUrl, uuid: 'chosen-gift-card-image' },
      group,
      quantity: 1,
      unitPrice: PriceModel.from(price).toDTO(),
      promotions: [],
      subtotal: price.price,
      netTotal: price.price,
      total: price.price,
      tax: MoneyModel.fromAmount(0),
      taxRate: 0,
      taxClass,
      message,
      recipientEmail,
      recipientName,
      senderName,
      imageUrl,
      giftMessage: message,
      giftCardRecipientEmail: recipientEmail,
      giftCardRecipientName: recipientName,
      giftCardSenderName: senderName,
      giftCardImageUrl: imageUrl,
      price: Number(price.price.amount)
    });
  }

  /** @inheritdoc */
  public override toDTO(): DTO<IGiftCardLineItem> {
    return {
      ...super.toDTO(),
      giftCardImageUrl: this.imageUrl,
      giftCardRecipientEmail: this.recipientEmail,
      giftCardRecipientName: this.recipientName,
      giftCardSenderName: this.senderName,
      giftMessage: this.message,
      price: this.price,
      imageUrl: this.imageUrl,
      recipientEmail: this.recipientEmail,
      recipientName: this.recipientName,
      senderName: this.senderName,
      message: this.message
    };
  }
}
